import { Injectable, inject, signal } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { DOMAIN_PATTERN, emailValidator, passwordValidator, phoneValidator, websiteValidator } from './validators';

/** Both wizard steps' forms live here so their values survive switching between the steps. */
@Injectable({ providedIn: 'root' })
export class RegistrationFormsService {
  private readonly fb = inject(NonNullableFormBuilder);

  readonly step1 = this.fb.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    // validator order decides which message shows first, as in the old app
    email: ['', [emailValidator, Validators.required]],
    password: ['', [Validators.required, passwordValidator]],
    phone: ['', [Validators.required, phoneValidator]],
  });

  readonly step2 = this.fb.group({
    companyName: ['', [Validators.required, Validators.maxLength(60)]],
    domain: ['', [Validators.required, Validators.pattern(DOMAIN_PATTERN), Validators.maxLength(59)]],
    country: ['', Validators.required],
    timeZone: ['', Validators.required],
    businessSector: ['', Validators.required],
    website: ['', [Validators.required, websiteValidator]],
    saleCurrency: [''],
  });

  readonly submitting = signal(false);

  markStepTouched(step: 1 | 2) {
    Object.values((step === 1 ? this.step1 : this.step2).controls).forEach((control) => {
      control.markAsTouched();
      control.markAsDirty();
      // no valueChanges: step 2 answers a country change by resetting the timezone the user picked
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  setDisabled(disabled: boolean) {
    for (const form of [this.step1, this.step2]) {
      if (disabled) {
        form.disable({ emitEvent: false });
      } else {
        form.enable({ emitEvent: false });
      }
    }
  }

  /** Any query param named like a control pre-fills it (never the password); the country code is lower-cased. */
  prefill(params: Record<string, string>) {
    for (const [key, value] of Object.entries(params)) {
      if (key === 'password') {
        continue;
      }
      const control = this.step1.get(key) ?? this.step2.get(key);
      control?.setValue(key === 'country' ? value.toLowerCase() : value);
    }
  }
}
